// @em/blocks — the single HTML-sanitization seam.
//
// Block views render host-authored HTML/Markdown, so every dangerouslySetInnerHTML
// payload goes through here. We sanitize with `sanitize-html` (a parser-based, DOM-
// free sanitizer) on BOTH the server and the client:
//
//  - It needs no DOM, so it runs identically on Node, in the browser, and — crucially
//    — on the Cloudflare Workers runtime that events-web deploys to. (DOMPurify needs
//    a real DOM; jsdom does not run on Workers, and the pure-JS DOMs that do, e.g.
//    linkedom, lack the APIs DOMPurify requires, so DOMPurify silently no-ops there.)
//  - Same library + same config on both sides => byte-identical output => no
//    hydration mismatch on the rendered HTML.
//
// Everything funnels through this one module, so the sanitizer is a single concern.
import sanitizeHtmlLib, { type IOptions } from 'sanitize-html';

// Rich-text profile: the tags Markdown (via `marked`) and host rich-text editors emit.
// Intentionally permissive for formatting; scripts, event handlers, and unsafe URL
// schemes are always stripped.
const RICH_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'div', 'span', 'br', 'hr',
  'a', 'img',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark', 'small', 'sub', 'sup', 'abbr',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'dl', 'dt', 'dd',
  'figure', 'figcaption',
  'input', // GFM task-list checkboxes emitted by `marked`
];

/**
 * A style DECLARATION VALUE we will keep. Everything is allowed except a functional notation that
 * makes the browser fetch or evaluate something: `url()` (a tracking pixel or an external font
 * fetched from an anonymous visitor's browser), plus the legacy IE/Gecko evaluators, which cost
 * nothing to exclude. `;{}<>` can never legitimately appear inside one declaration's value and are
 * refused so a value cannot close out of its own declaration.
 *
 * ⚠ This is a value filter, NOT the safety boundary — that is the PROPERTY allowlist below. Its job
 * is only to stop an otherwise-fine property smuggling a network request.
 */
const SAFE_STYLE_VALUE = /^(?!.*(?:url|expression|image-set|-moz-binding|element)\s*\()[^;{}<>]*$/i;

const styleProps = (...names: string[]): Record<string, RegExp[]> =>
  Object.fromEntries(names.map((n) => [n, [SAFE_STYLE_VALUE]]));

/**
 * ⚠⚠ AN ALLOWLIST, AND IT MUST STAY ONE (owner ruling, 2026-09-22).
 *
 * Host-authored HTML is rendered to ANONYMOUS VISITORS on the public event site. Before this,
 * `style` was allowed on every tag with no property restriction at all, which meant a single
 * authored div —
 *
 *     <div style="position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:9999"></div>
 *
 * — covered the ENTIRE public event page and swallowed every click on it. That is not XSS (CSS
 * execution vectors are long dead in modern browsers) and it is the host's own page, but it is a
 * whole-page outage authored by accident as easily as on purpose, and nothing about the editor
 * would have hinted at it.
 *
 * The properties below are the ones rich text actually uses: type, colour, spacing, borders,
 * in-flow sizing, lists, tables. What is DELIBERATELY ABSENT is everything that takes an element
 * OUT of normal flow or off the page — `position`, `inset`/`top`/`right`/`bottom`/`left`,
 * `z-index`, `transform`, `overflow`, `visibility`, `pointer-events`, `clip-path`, `filter`,
 * `mix-blend-mode`, `animation`, `transition`, `will-change`. In normal flow an oversized block can
 * look wrong; it cannot cover the page or intercept a click meant for something else.
 *
 * ⚠ DO NOT convert this to a denylist of the dangerous properties. That is the exact shape this
 * project has already been burned by (`accountUpdate`'s `omit(params, [...])`, which let any host
 * grant themselves a plan): everything added to CSS later is permitted by default, and nothing
 * errors. Adding a property here is a deliberate line in a diff; that is the point.
 */
const RICH_STYLES: IOptions['allowedStyles'] = {
  '*': styleProps(
    // type
    'color', 'font', 'font-size', 'font-weight', 'font-style', 'font-family', 'font-variant',
    'line-height', 'letter-spacing', 'word-spacing', 'white-space', 'text-align', 'text-decoration',
    'text-decoration-color', 'text-decoration-line', 'text-decoration-style', 'text-transform',
    'text-indent', 'text-shadow', 'vertical-align', 'direction', 'word-break', 'overflow-wrap',
    // surface
    'background-color', 'opacity', 'box-shadow',
    // spacing
    'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
    'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
    // edges
    'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
    'border-color', 'border-style', 'border-width', 'border-radius', 'border-collapse', 'border-spacing',
    // in-flow sizing only
    'width', 'min-width', 'max-width', 'height', 'min-height', 'max-height', 'box-sizing',
    // flow
    'display', 'float', 'clear',
    // lists + tables
    'list-style', 'list-style-type', 'list-style-position', 'table-layout', 'caption-side',
  ),
};

const RICH_ATTRS: IOptions['allowedAttributes'] = {
  // ⚠ NO `id` (owner ruling, 2026-09-22). An authored id lands in the SAME document as the event
  // site's own chrome, and the site looks its elements up by id — `DirectMessagesLauncher` does
  // `document.getElementById('em-dm-actionbar-slot')` to find where to portal itself. A host who
  // writes that id in a rich-text block captures the launcher's slot, and the failure appears in a
  // component nobody edited. Nothing in blocks or events-web needs authored ids: `marked` emits no
  // heading ids here, and no block renders an in-page `href="#…"`.
  '*': ['class', 'style', 'title', 'dir', 'lang', 'align'],
  a: ['href', 'name', 'target', 'rel'],
  img: ['src', 'alt', 'width', 'height', 'loading', 'srcset', 'sizes'],
  input: ['type', 'checked', 'disabled'],
  ol: ['start', 'type'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan', 'scope'],
  col: ['span'],
  colgroup: ['span'],
};

/**
 * The colours a highlight with NO colour of its own (`<mark>` with no background) is given (A2e #80).
 *
 * ⚠ Without this a bare mark is unreadable on a DARK event theme: the site renders host text inside Mantine's
 * Typography, whose light-scheme rule (events-web always runs Mantine light) is `background: yellow-2; color: inherit`
 * — the theme's PALE ink on pale yellow. Bare marks are common: every highlight saved before #80, Mod-Shift-H and the
 * `==text==` shortcut all write one. Mantine's yellow-2 is what the host saw in the editor; the ink is fixed black.
 * The API's `emailSafeHighlights` gives an email the same colours.
 */
export const BARE_HIGHLIGHT_STYLE = 'background-color:#fff3bf;color:#000000';

/** A `<mark>` keeps its own style; one with no background gets BARE_HIGHLIGHT_STYLE in front of whatever it has. */
const markWithColours: sanitizeHtmlLib.Transformer = (tagName, attribs) => {
  const style = String(attribs.style ?? '');

  if (/(^|;)\s*background(-color)?\s*:/i.test(style)) {
    return { tagName, attribs };
  }

  return { tagName, attribs: { ...attribs, style: style ? `${BARE_HIGHLIGHT_STYLE};${style}` : BARE_HIGHLIGHT_STYLE } };
};

const BASE: IOptions = {
  allowedTags: RICH_TAGS,
  allowedAttributes: RICH_ATTRS,
  allowedStyles: RICH_STYLES,
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  // Markdown often inlines images as data: URIs; allow that for <img> only.
  allowedSchemesByTag: { img: ['http', 'https', 'data'] },
  allowProtocolRelative: true,
  // Every link in host-authored content is treated as untrusted, user-generated
  // outbound: `nofollow ugc` so it never passes SEO equity onward (spam/paid-link
  // safe), plus `noopener noreferrer` to harden new-tab links. Overwrites any rel the
  // author set. Applies to all rich-text / markdown / HTML / embed block bodies.
  //
  // ⚠ merge MUST be `true`. With `false` simpleTransform REPLACES the whole attribute
  // set, which silently dropped `href` from EVERY link a host wrote in a rich-text,
  // markdown, HTML or embed block — "visit our site" rendered as unclickable text on
  // the public event website. Fixed 2026-09-21; it had been noted in this file as "a
  // separate bug" and never actioned.
  // ⚠ Until that fix, no anchor kept an href, so `allowedSchemes` had nothing to
  // filter and was dormant. It is LOAD-BEARING again now: it is the only thing
  // stopping `<a href="javascript:…">` in host-authored content shown to anonymous
  // visitors. Guarded by `pnpm run check-partner-summary`.
  transformTags: {
    a: sanitizeHtmlLib.simpleTransform('a', { rel: 'nofollow noopener noreferrer ugc' }, true),
    mark: markWithColours,
  },
};

/** Sanitize host-authored HTML (text/markdown/richtext block bodies, card and item
 *  descriptions). Strips scripts, event handlers, and unsafe URL schemes. */
export const sanitizeHtml = (html: string): string => sanitizeHtmlLib(html, BASE);

// STRICT profile for GUEST/host COMMENTS (untrusted, adversarial user content) — much tighter than the rich
// block profile: no images, tables, iframes, ids, classes, or `style` (no CSS/layout abuse, no tracking
// pixels), only basic formatting + safe links. Video embeds are handled OUT of band (a React facade), never
// via raw HTML in the comment.
const COMMENT_TAGS = [
  'p', 'br', 'hr',
  'a',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'strong', 'b', 'em', 'i', 'u', 's', 'del', 'mark',
  'h3', 'h4', 'h5', 'h6',
];

/** Sanitize a rendered-markdown COMMENT. Strips everything but basic formatting + safe (nofollow, new-tab)
 *  links; no images/iframes/tables/styles. */
export const sanitizeCommentHtml = (html: string): string =>
  sanitizeHtmlLib(html, {
    allowedTags: COMMENT_TAGS,
    allowedAttributes: { a: ['href', 'target', 'rel'] },
    allowedSchemes: ['http', 'https', 'mailto'],
    allowProtocolRelative: false,
    transformTags: {
      // merge:true keeps the link's href (merge:false would DROP it — non-clickable) while forcing safe rel +
      // new-tab. ⚠ The rich profile above used merge:false for months and silently stripped href from EVERY
      // host-authored link; fixed 2026-09-21. Both profiles now merge, and neither may stop.
      a: sanitizeHtmlLib.simpleTransform('a', { rel: 'nofollow noopener noreferrer ugc', target: '_blank' }, true),
    },
  });

/** Sanitize pasted embed HTML, keeping iframes — the whole point of an embed block. */
export const sanitizeEmbedHtml = (html: string): string =>
  sanitizeHtmlLib(html, {
    ...BASE,
    allowedTags: [...RICH_TAGS, 'iframe'],
    allowedAttributes: {
      ...RICH_ATTRS,
      iframe: [
        'src', 'width', 'height', 'name', 'title', 'style',
        'allow', 'allowfullscreen', 'frameborder', 'scrolling', 'loading', 'referrerpolicy',
      ],
    },
    allowedSchemesByTag: { img: ['http', 'https', 'data'], iframe: ['http', 'https'] },
  });
